'use client';

import React, { useState } from 'react';

import Rating from '@icons/Rating.svg';

import { StarRatingProps } from './interfaces/StarRating';

import { StarContainer, StarWrapper } from './styles/StarRating';

/**
 * Interactive star rating input that allows users to provide a 1-5 rating.
 */
export const StarRating: React.FC<StarRatingProps> = ({
  rating,
  onRatingChange,
  className,
  readOnly = false,
  size,
  gap,
}) => {
  const [hoveredRating, setHoveredRating] = useState(0);

  const handleStarClick = (starIndex: number) => {
    if (readOnly || !onRatingChange) return;
    const newRating = starIndex + 1;
    onRatingChange(newRating);
  };

  const handleStarHover = (starIndex: number) => {
    if (readOnly) return;
    setHoveredRating(starIndex + 1);
  };

  const handleStarLeave = () => {
    if (readOnly) return;
    setHoveredRating(0);
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => {
      const isActive = rating > index;
      const isHovered = hoveredRating > index;

      return (
        <StarWrapper
          key={index}
          $isActive={isActive}
          $isHovered={isHovered}
          $readOnly={readOnly}
          $size={size}
          onClick={readOnly ? undefined : () => handleStarClick(index)}
          onMouseEnter={readOnly ? undefined : () => handleStarHover(index)}
          onMouseLeave={readOnly ? undefined : handleStarLeave}
          type="button"
          aria-pressed={isActive}
          aria-label={readOnly ? `${rating} out of 5 stars` : `Rate ${index + 1} stars`}>
          <Rating />
        </StarWrapper>
      );
    });
  };

  return (
    <StarContainer className={className} $readOnly={readOnly} $gap={gap}>
      {renderStars()}
    </StarContainer>
  );
};
